/**
 * Persona Engine React Query Hooks
 *
 * Query and mutation hooks for all Persona Engine API endpoints.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@personic/shared'
import type {
  PersonaEngineInfo,
  PersonaEngineArchetype,
  PersonaEngineConfig,
  PersonaEngineConfigUpdate,
  PersonaEngineRenderRequest,
  PersonaEngineRenderResponse,
  PersonaEngineLogResponse,
  PersonaEngineHealthResponse,
  ArchetypeCategory,
  EngineType,
} from '@personic/shared/types/persona-engine'

// =============================================================================
// QUERY KEYS
// =============================================================================

const QUERY_KEYS = {
  engines: ['persona-engine', 'engines'] as const,
  archetypes: (category?: ArchetypeCategory) =>
    category ? ['persona-engine', 'archetypes', category] as const : ['persona-engine', 'archetypes'] as const,
  archetype: (id: string) => ['persona-engine', 'archetype', id] as const,
  config: ['persona-engine', 'config'] as const,
  health: ['persona-engine', 'health'] as const,
  generationLog: (filters?: Record<string, unknown>) =>
    ['persona-engine', 'generation-log', filters] as const,
  compute: (profileId: number) => ['persona-engine', 'compute', profileId] as const,
}

// =============================================================================
// QUERY HOOKS
// =============================================================================

export function usePersonaEngineEngines() {
  return useQuery({
    queryKey: QUERY_KEYS.engines,
    queryFn: async (): Promise<PersonaEngineInfo[]> => {
      const { data } = await api.get('/persona-engine/engines')
      return data
    },
    staleTime: 5 * 60 * 1000, // Engines don't change often
  })
}

export function usePersonaEngineArchetypes(category?: ArchetypeCategory) {
  return useQuery({
    queryKey: QUERY_KEYS.archetypes(category),
    queryFn: async (): Promise<PersonaEngineArchetype[]> => {
      const params = category ? { category } : {}
      const { data } = await api.get('/persona-engine/archetypes', { params })
      return data
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function usePersonaEngineArchetype(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.archetype(id),
    queryFn: async (): Promise<PersonaEngineArchetype> => {
      const { data } = await api.get(`/persona-engine/archetypes/${id}`)
      return data
    },
    enabled: !!id,
  })
}

export function usePersonaEngineConfig() {
  return useQuery({
    queryKey: QUERY_KEYS.config,
    queryFn: async (): Promise<PersonaEngineConfig> => {
      const { data } = await api.get('/persona-engine/config')
      return data
    },
  })
}

export function usePersonaEngineHealth() {
  return useQuery({
    queryKey: QUERY_KEYS.health,
    queryFn: async (): Promise<PersonaEngineHealthResponse> => {
      const { data } = await api.get('/persona-engine/health')
      return data
    },
    staleTime: 30 * 1000,
  })
}

export function usePersonaEngineLog(filters?: {
  limit?: number
  offset?: number
  engine?: EngineType
  min_confidence?: number
  max_confidence?: number
}) {
  return useQuery({
    queryKey: QUERY_KEYS.generationLog(filters),
    queryFn: async (): Promise<PersonaEngineLogResponse> => {
      const { data } = await api.get('/persona-engine/generation-log', { params: filters })
      return data
    },
  })
}

// =============================================================================
// MUTATION HOOKS
// =============================================================================

export function useUpdatePersonaEngineConfig() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (update: PersonaEngineConfigUpdate): Promise<PersonaEngineConfig> => {
      const { data } = await api.put('/persona-engine/config', update)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.config })
    },
  })
}

export function usePersonaEngineRender() {
  return useMutation({
    mutationFn: async (request: PersonaEngineRenderRequest): Promise<PersonaEngineRenderResponse> => {
      const { data } = await api.post('/persona-engine/render', request)
      return data
    },
  })
}

// =============================================================================
// v3.0 HOOKS — Instances, Graduation, Role Templates
// =============================================================================

export function usePersonaEngineInstances() {
  return useQuery({
    queryKey: ['persona-engine', 'instances'] as const,
    queryFn: async () => {
      const { data } = await api.get('/persona-engine/instances')
      return data as Array<{
        persona_id: string
        instance_name: string | null
        archetype_id: string | null
        archetype_category: string | null
        graduation_stage: string
        graduation_dimensions_derived: number
        communication_briefing: Record<string, unknown>
        product_context: string
        is_current: boolean
        archetype_version: number | null
        created_at: string | null
      }>
    },
  })
}

export function usePersonaEngineTenantInstances() {
  return useQuery({
    queryKey: ['persona-engine', 'instances', 'tenant'] as const,
    queryFn: async () => {
      const { data } = await api.get('/persona-engine/instances', { params: { scope: 'tenant' } })
      return data as Array<{
        persona_id: string
        instance_name: string | null
        archetype_id: string | null
        archetype_category: string | null
        graduation_stage: string
        graduation_dimensions_derived: number
        communication_briefing: Record<string, unknown>
        product_context: string
        is_current: boolean
        archetype_version: number | null
        created_at: string | null
      }>
    },
  })
}

export function usePersonaEngineGraduation(personaId: string | null) {
  return useQuery({
    queryKey: ['persona-engine', 'graduation', personaId] as const,
    queryFn: async () => {
      const { data } = await api.get(`/persona-engine/graduation/${personaId}`)
      return data as {
        stage: string
        dimensions_derived: number
        total_dimensions: number
        dimension_details: Array<{
          dimension: string
          archetype_default: number | null
          current_value: number | null
          is_profile_derived: boolean
          source: string
        }>
        persona_id: string | null
        instance_name: string | null
        archetype_id: string | null
      }
    },
    enabled: !!personaId,
  })
}

export function usePersonaEngineRoleTemplates(productContext?: string) {
  return useQuery({
    queryKey: ['persona-engine', 'roles', productContext] as const,
    queryFn: async () => {
      const params = productContext ? { product_context: productContext } : {}
      const { data } = await api.get('/persona-engine/roles', { params })
      return data as Array<{
        role_template_id: string
        display_name: string
        description: string
        product_context: string
        domain_directives: unknown[]
        avoidance_rules: unknown[]
        dimension_nudges: Record<string, number>
        guardrail_extensions: Record<string, unknown>
        is_active: boolean
        tenant_id: string | null
        version: number
      }>
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function usePersonaEngineRoleTemplate(roleTemplateId: string | null) {
  return useQuery({
    queryKey: ['persona-engine', 'role', roleTemplateId] as const,
    queryFn: async () => {
      const { data } = await api.get(`/persona-engine/roles/${roleTemplateId}`)
      return data
    },
    enabled: !!roleTemplateId,
  })
}

export function useCreateRoleTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (template: {
      role_template_id: string
      display_name: string
      description: string
      product_context?: string
      domain_directives?: unknown[]
      avoidance_rules?: unknown[]
      dimension_nudges?: Record<string, number>
      guardrail_extensions?: Record<string, unknown>
    }) => {
      const { data } = await api.post('/persona-engine/roles', template)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['persona-engine', 'roles'] })
    },
  })
}

export function useUpdateRoleTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...update }: {
      id: string
      display_name?: string
      description?: string
      domain_directives?: unknown[]
      avoidance_rules?: unknown[]
      dimension_nudges?: Record<string, number>
      guardrail_extensions?: Record<string, unknown>
    }) => {
      const { data } = await api.put(`/persona-engine/roles/${id}`, update)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['persona-engine', 'roles'] })
    },
  })
}

export function useDeleteRoleTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (roleTemplateId: string) => {
      await api.delete(`/persona-engine/roles/${roleTemplateId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['persona-engine', 'roles'] })
    },
  })
}
